

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewTrainingModuleModal from 'components/NewTrainingModuleModal';
import MDBadge from "components/MDBadge";

const TrainingModules = () => {
    const [modules, setModules] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('training_modules');

    useEffect(() => {
        fetchModules();
    }, []);

    const fetchModules = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/training-modules/');
            setModules(response.data);
        } catch (error) {
            console.error('Error fetching training modules:', error);
            setSnackbar({ open: true, message: 'Error loading Training Modules', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveModule = async (payload) => {
        try {
            await axiosInstance.post('/training-modules/', payload);
            setSnackbar({ open: true, message: 'Training Module created successfully', severity: 'success' });
            fetchModules();
        } catch (error) {
            console.error('Error saving Training Module:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/training-modules/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Training Title",
            accessor: "name",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        { Header: "Intended For", accessor: "training_for" },
        {
            Header: "Link",
            accessor: "content_url",
            Cell: ({ value }) => (
                <MDTypography variant="caption" color="info" component="a" href={value} target="_blank" rel="noreferrer">
                    View Content
                </MDTypography>
            )
        },
        {
            Header: "Active",
            accessor: "active",
            Cell: ({ value }) => (
                <MDBadge
                    badgeContent={value ? "Yes" : "No"}
                    color={value ? "success" : "secondary"}
                    variant="gradient"
                    size="sm"
                />
            )
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: modules,
    }), [modules]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Training Modules</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Module
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : modules.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Training Module" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewTrainingModuleModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveModule}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TrainingModules;
